import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Cookies from "js-cookie";
import { AUTH_COOKIE_NAME } from "@knitknot/shared/constants";
import { useWorkspaceStore } from '@/hooks/state';
import { userService, getUserIdFromJWT } from "@/services/auth";

/**
 * Protected route wrapper.
 *
 * Routing decision tree:
 *   Has auth cookie?
 *   NO  --> /signin
 *   YES --> render page
 *
 * The JWT's `sub` claim is the authoritative users.id — hooks read it via
 * getUserIdFromJWT() and the backend resolves it on every request. This
 * component's only other job is to enrich the Zustand store with avatar /
 * profile-image fields (which the JWT doesn't carry), so the header can
 * render them without a flash. Transient failures (5xx, network) are
 * swallowed — the user keeps whatever's in the store already.
 */
const ProtectedRoute = ({ children }: { children: React.ReactNode }) => {
  const navigate = useNavigate();
  const { userInfo, setUserInfo, workspaceId } = useWorkspaceStore();
  // Cookie presence is synchronously knowable — seed from it so an
  // authenticated user renders on the FIRST frame instead of one null frame
  // (perf plan §4.12); the effect still owns the redirect for the negative.
  const [authChecked, setAuthChecked] = useState(() => !!Cookies.get(AUTH_COOKIE_NAME));

  useEffect(() => {
    const authToken = Cookies.get(AUTH_COOKIE_NAME);
    const isAuthenticated = !!authToken || !!workspaceId;

    if (!isAuthenticated) {
      navigate("/signin");
      return;
    }

    setAuthChecked(true);

    // Enrich the store with avatar/profile-image fields once. Everything
    // else (id, email, firstName, lastName) is already populated by the
    // sign-in response. Skip if avatar is already present.
    if (userInfo?.avatar_id !== undefined) {
      return;
    }

    const userId = getUserIdFromJWT();
    if (!userId) return;

    userService
      .getUserById(userId)
      .then((fullUser) => {
        if (!fullUser) return;
        setUserInfo({
          email: fullUser.username,
          firstName: fullUser.first_name,
          lastName: fullUser.last_name,
          avatar_id: fullUser.avatar_id,
          profile_image_url: fullUser.profile_image_url,
        });
      })
      .catch(() => {
        // Transient failure — leave the store alone. We must never log
        // the user out over a 5xx/network blip.
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate, workspaceId]);

  if (authChecked) {
    return children;
  }

  // Brief null render while checking auth (prevents flash)
  return null;
};

export default ProtectedRoute;
